import React, { ReactNode, useCallback } from "react";
import { FlatList, ListRenderItemInfo, View } from "react-native";
import {
  BookingItem,
  SessionItem,
} from "@teachloop/backend/api/app/student/schedule/queries";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import StudentSessionCard from "../shared/student-session-card";
import StudentBookingCard from "../shared/student-booking-card";
import ScheduleEmptyState from "./schedule-empty-state";

function StudentScheduleList({
  scheduleList,
  header,
  statusFilter,
}: {
  scheduleList: SessionItem[] | BookingItem[];
  header: ReactNode;
  statusFilter?: "upcoming" | "requests" | "completed";
}) {
  const responsiveWidth = useResponsiveWidth();

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<SessionItem | BookingItem>) => {
      const renderCard = () => {
        switch (item.type) {
          case "session":
            return (
              <StudentSessionCard
                key={item.id}
                session={{
                  _id: item.id,
                  subject: item.subject,
                  type: item.sessionType,
                  deliveryMethod: item.deliveryMethod,
                  startTime: item.startTime,
                  endTime: item.endTime,
                  tutor: {
                    ...item.tutor,
                    firstName: item.tutor.name?.split(" ")?.[0],
                    lastName: item.tutor.name?.split(" ")?.[1],
                  },
                  status: item.status,
                  url: item.url,
                }}
              />
            );
          case "booking":
            return (
              <StudentBookingCard
                key={item.id}
                booking={{
                  _id: item.id,
                  subject: item.subject,
                  type: item.deliveryMethod,
                  startTime: item.startTime,
                  endTime: item.endTime,
                  tutor: {
                    ...item.tutor,
                    firstName: item.tutor.name.split(" ")[0],
                    lastName: item.tutor.name.split(" ")[1],
                  },
                  recurringGroupId: item.recurringGroupId,
                }}
              />
            );

          default:
            return null;
        }
      };
      return <View className="mx-5">{renderCard()}</View>;
    },
    []
  );

  const getEmptyStateMessage = () => {
    switch (statusFilter) {
      case "upcoming":
        return "You have no upcoming lessons scheduled.";
      case "requests":
        return "You have no pending booking requests.";
      case "completed":
        return "You have no completed lessons yet.";
      default:
        return "You have no lessons scheduled.";
    }
  };

  const ListEmptyComponent = useCallback(() => {
    return <ScheduleEmptyState message={getEmptyStateMessage()} />;
  }, [statusFilter]);

  return (
    <FlatList
      data={scheduleList}
      renderItem={renderItem}
      ListHeaderComponent={() => header}
      ListEmptyComponent={ListEmptyComponent}
      ItemSeparatorComponent={() => <View className="h-4" />}
      ListFooterComponent={() => <View className="h-4" />}
      style={{ width: responsiveWidth, alignSelf: "center" }}
    />
  );
}

export default StudentScheduleList;
